import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Heading } from "catalyst/heading";
import { buildLinkQrUrl, apiStartEmailRegistration, apiFinishEmailRegistration, apiReissueLoginKey, apiRemovePasskey, apiRemoveEmail, apiSetUserName, urlApiPasskeyList, urlApiProfile, urlApiEmailList, useProfile, usePasskeys, useUserEmails, } from "../lib/api.js";
import { registerPasskey } from "../lib/passkey.js";
import { dateTimeFormatter } from "../lib/util.js";
import { LoadingErrorBadge, LoadingBadge } from "../components/badges.js";
import { DescriptionDetails, DescriptionList, DescriptionTerm, } from "catalyst/description-list";
import { Description, Field, Label } from "catalyst/fieldset";
import { CopyableValue } from "../components/CopyableValue.js";
import { Badge } from "catalyst/badge";
import { Button } from "catalyst/button";
import { Text } from "catalyst/text";
import { useEffect, useRef, useState } from "react";
import { useSWRConfig } from "swr";
import { Alert, AlertActions, AlertBody, AlertDescription, AlertTitle, } from "catalyst/alert";
import { useLocation, useSearchParams } from "wouter";
import { useHistoryState } from "wouter/use-browser-location";
import { QrCodeIcon } from "@heroicons/react/20/solid";
import { EmailAuthForm } from "../components/EmailAuthForm.js";
import { emailErrorMessage } from "../lib/email.js";
import { useAccount } from "coreinfra-ui/account-context";
import { AccountHubs } from "../components/AccountHubs.js";
import { PageTabs } from "../components/PageTabs.js";
import { InlineNameEditor } from "../components/InlineNameEditor.js";
const accountDetailsClassName = "col-span-full grid min-w-0 grid-cols-subgrid sm:col-span-2";
const accountActionClassName = "col-start-1 mt-2 self-start justify-self-start whitespace-nowrap sm:col-start-2 sm:row-start-1 sm:mt-0";
const EmailSection = () => {
    const [searchParams] = useSearchParams();
    const { data, error, isLoading } = useUserEmails();
    const { mutate } = useSWRConfig();
    const [, navigate] = useLocation();
    const historyState = useHistoryState();
    const mounted = useRef(false);
    const [emailDraft, setEmailDraft] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [challenge, setChallenge] = useState(null);
    const [verifiedEmailId, setVerifiedEmailId] = useState(null);
    const [actionError, setActionError] = useState(null);
    const [confirmingRemove, setConfirmingRemove] = useState(null);
    const current = data?.emails.find((email) => email.verified_at != null) ??
        (historyState?.emailRegistrationCode ? challenge?.email : undefined) ??
        data?.emails.find((email) => email.email === emailDraft) ??
        data?.emails[0];
    const isVerified = current?.verified_at != null ||
        (current !== undefined && current.id === verifiedEmailId);
    const showCode = !isVerified &&
        challenge !== null &&
        historyState?.emailRegistrationCode === true;
    useEffect(() => {
        mounted.current = true;
        if (window.history.state?.emailRegistrationCode) {
            window.history.replaceState(null, "");
        }
        return () => {
            mounted.current = false;
        };
    }, []);
    useEffect(() => {
        setActionError(null);
    }, [showCode]);
    const requestVerification = async (email) => {
        const normalizedEmail = email.trim().toLowerCase();
        if (submitting || !normalizedEmail)
            return;
        setSubmitting(true);
        setActionError(null);
        setChallenge(null);
        try {
            const result = await apiStartEmailRegistration(normalizedEmail);
            void mutate(urlApiProfile).catch(() => { });
            if (!mounted.current)
                return;
            if (result.challenge_id && result.expires_at) {
                setChallenge({
                    email: result.email,
                    challenge_id: result.challenge_id,
                    expires_at: result.expires_at,
                    retry_after_secs: result.retry_after_secs,
                });
            }
            else {
                setChallenge(null);
                if (result.email.verified_at == null) {
                    setActionError("Подождите немного и попробуйте еще раз.");
                }
            }
            await mutate(urlApiEmailList, {
                emails: [
                    result.email,
                    ...(data?.emails ?? []).filter((email) => email.id !== result.email.id),
                ],
            }, { revalidate: false });
            setEmailDraft(result.email.email);
            if (mounted.current && result.challenge_id && result.expires_at) {
                navigate(window.location.pathname + window.location.search, {
                    state: { emailRegistrationCode: true },
                });
            }
        }
        catch (err) {
            if (!mounted.current)
                return;
            setActionError(emailErrorMessage(err));
            // The email may have been saved before delivery (or the response) failed.
            void mutate(urlApiEmailList).catch(() => { });
        }
        finally {
            if (mounted.current)
                setSubmitting(false);
        }
    };
    const verifyEmail = async (code) => {
        if (!challenge || submitting || !/^[0-9]{6}$/.test(code))
            return;
        setSubmitting(true);
        setActionError(null);
        try {
            await apiFinishEmailRegistration(challenge.challenge_id, code, challenge.email.email);
            void mutate(urlApiProfile).catch(() => { });
            if (!mounted.current)
                return;
            setVerifiedEmailId(challenge.email.id);
            setEmailDraft(challenge.email.email);
            setChallenge(null);
            navigate(window.location.pathname + window.location.search, {
                replace: true,
            });
            // The code is consumed already. A refresh failure must not undo success.
            void mutate(urlApiEmailList).catch(() => { });
        }
        catch (err) {
            if (mounted.current)
                setActionError(emailErrorMessage(err));
        }
        finally {
            if (mounted.current)
                setSubmitting(false);
        }
    };
    const removeEmail = async () => {
        if (!confirmingRemove || submitting)
            return;
        setSubmitting(true);
        setActionError(null);
        try {
            await apiRemoveEmail(confirmingRemove.id);
            void mutate(urlApiProfile).catch(() => { });
            setChallenge(null);
            setVerifiedEmailId(null);
            setEmailDraft(null);
            await mutate(urlApiEmailList, {
                emails: data?.emails.filter((email) => email.id !== confirmingRemove.id) ??
                    [],
            }, { revalidate: false });
            setConfirmingRemove(null);
            if (window.history.state?.emailRegistrationCode) {
                window.history.replaceState(null, "");
            }
        }
        catch (err) {
            setActionError(emailErrorMessage(err));
        }
        finally {
            setSubmitting(false);
        }
    };
    if (!data)
        return isLoading ? _jsx(LoadingBadge, {}) : _jsx(LoadingErrorBadge, {});
    return (_jsxs("div", { className: "col-span-full grid min-w-0 grid-cols-subgrid gap-y-6", children: [error && (_jsx("div", { className: "col-span-full", children: _jsx(LoadingErrorBadge, {}) })), current && isVerified ? (_jsxs("div", { className: "col-span-full grid grid-cols-subgrid items-start gap-y-2", children: [_jsxs("div", { className: "flex min-w-0 flex-wrap items-center self-center gap-x-3 gap-y-2", children: [_jsx(Text, { className: "min-w-0 break-all text-zinc-950! dark:text-white!", children: current.email }), _jsx(Badge, { role: "status", className: "max-w-full whitespace-normal!", color: "lime", children: "\u041F\u043E\u0434\u0442\u0432\u0435\u0440\u0436\u0434\u0435\u043D" })] }), _jsx(Button, { plain: true, className: accountActionClassName, disabled: submitting, onClick: () => {
                            setActionError(null);
                            setConfirmingRemove(current);
                        }, children: "\u0423\u0434\u0430\u043B\u0438\u0442\u044C" }), _jsxs(Text, { role: "status", className: "col-span-full col-start-1 min-w-0 sm:col-span-1 sm:col-start-1", children: ["\u0414\u043E\u0431\u0430\u0432\u043B\u0435\u043D: ", formatTimestamp(current.added_at)] })] })) : (_jsx(EmailAuthForm, { autoFocus: searchParams.get("focus") === "email", buttonPlain: true, subgrid: true, email: showCode
                    ? challenge.email.email
                    : (emailDraft ?? current?.email ?? ""), emailLabel: "Email \u0430\u043A\u043A\u0430\u0443\u043D\u0442\u0430", challenge: showCode ? challenge : null, submitting: submitting, error: confirmingRemove ? null : actionError, buttonLabel: showCode ? "Подтвердить" : "Добавить", onEmailChange: setEmailDraft, onClearError: () => setActionError(null), onSubmit: (value) => {
                    if (showCode)
                        void verifyEmail(value);
                    else
                        void requestVerification(value);
                } })), actionError && isVerified && !confirmingRemove && (_jsx(Text, { role: "alert", className: "col-span-full text-red-600! dark:text-red-500!", children: actionError })), _jsxs(Alert, { open: confirmingRemove !== null, onClose: (open) => {
                    if (submitting)
                        return;
                    if (!open)
                        setConfirmingRemove(null);
                }, children: [_jsx(AlertTitle, { children: "\u0423\u0434\u0430\u043B\u0438\u0442\u044C email?" }), _jsxs(AlertDescription, { children: ["\u0412\u0445\u043E\u0434 \u043F\u043E \u0430\u0434\u0440\u0435\u0441\u0443 ", confirmingRemove?.email, " \u0441\u0440\u0430\u0437\u0443 \u043F\u0435\u0440\u0435\u0441\u0442\u0430\u043D\u0435\u0442 \u0440\u0430\u0431\u043E\u0442\u0430\u0442\u044C."] }), actionError && (_jsx(Text, { role: "alert", className: "mt-3 text-red-600! dark:text-red-500!", children: actionError })), _jsxs(AlertActions, { children: [_jsx(Button, { plain: true, disabled: submitting, onClick: () => setConfirmingRemove(null), children: "\u041E\u0442\u043C\u0435\u043D\u0430" }), _jsx(Button, { color: "red", disabled: submitting, onClick: () => void removeEmail(), children: submitting ? "Удаляем…" : "Удалить" })] })] })] }));
};
const formatTimestamp = (unixSeconds) => {
    if (unixSeconds == null)
        return "—";
    return dateTimeFormatter.format(new Date(unixSeconds * 1000));
};
const PasskeySection = () => {
    const { data, error, isLoading } = usePasskeys();
    const { mutate } = useSWRConfig();
    const [submitting, setSubmitting] = useState(false);
    const [alertError, setAlertError] = useState(null);
    const [removingId, setRemovingId] = useState(null);
    const [confirmingRemove, setConfirmingRemove] = useState(null);
    const onAdd = async () => {
        setSubmitting(true);
        setAlertError(null);
        try {
            await registerPasskey();
            void mutate(urlApiProfile).catch(() => { });
            await mutate(urlApiPasskeyList);
        }
        catch (err) {
            setAlertError({
                title: "Не удалось добавить passkey",
                message: err instanceof Error ? err.message : String(err),
            });
        }
        finally {
            setSubmitting(false);
        }
    };
    const onRemove = async (item) => {
        setRemovingId(item.id);
        setAlertError(null);
        try {
            await apiRemovePasskey({ id: item.id });
            void mutate(urlApiProfile).catch(() => { });
            await mutate(urlApiPasskeyList);
            setConfirmingRemove(null);
        }
        catch (err) {
            setAlertError({
                title: "Не удалось удалить passkey",
                message: err instanceof Error ? err.message : String(err),
            });
        }
        finally {
            setRemovingId(null);
        }
    };
    const passkeys = data?.passkeys ?? [];
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "col-span-full grid grid-cols-subgrid", children: [isLoading && _jsx(LoadingBadge, {}), error && _jsx(LoadingErrorBadge, {}), data && passkeys.length === 0 && (_jsx(Text, { className: "col-span-full sm:col-span-1 sm:col-start-1", children: "\u041D\u0435\u0442 \u0434\u043E\u0431\u0430\u0432\u043B\u0435\u043D\u043D\u044B\u0445 passkey." })), data && passkeys.length > 0 && (_jsx("ul", { className: "col-span-full grid grid-cols-subgrid", children: passkeys.map((p) => (_jsxs("li", { className: "col-span-full grid grid-cols-subgrid border-b border-zinc-950/5 py-3 first:pt-0 last:border-0 last:pb-0 dark:border-white/5", children: [_jsx(Text, { className: "min-w-0 self-center break-words font-medium text-zinc-950! dark:text-white!", children: p.name }), _jsx(Button, { plain: true, className: accountActionClassName, disabled: removingId === p.id, onClick: () => setConfirmingRemove(p), children: "\u0423\u0434\u0430\u043B\u0438\u0442\u044C" }), _jsx("div", { className: "col-span-full col-start-1 min-w-0 sm:col-span-1 sm:col-start-1", children: _jsxs(Text, { children: ["\u0414\u043E\u0431\u0430\u0432\u043B\u0435\u043D: ", formatTimestamp(p.added_at)] }) })] }, p.id))) }))] }), _jsx("div", { className: "col-span-full col-start-1 mt-6 sm:col-span-1 sm:col-start-1", children: _jsx(Button, { outline: true, disabled: submitting, onClick: onAdd, children: "\u0414\u043E\u0431\u0430\u0432\u0438\u0442\u044C passkey" }) }), _jsxs(Alert, { open: confirmingRemove !== null, onClose: (open) => {
                    if (removingId)
                        return;
                    if (!open)
                        setConfirmingRemove(null);
                }, children: [_jsxs(AlertDescription, { children: ["Passkey \u00AB", confirmingRemove?.name, "\u00BB \u0431\u0443\u0434\u0435\u0442 \u0443\u0434\u0430\u043B\u0435\u043D. \u0412\u0445\u043E\u0434 \u0441 \u0435\u0433\u043E \u043F\u043E\u043C\u043E\u0449\u044C\u044E \u0431\u043E\u043B\u044C\u0448\u0435 \u043D\u0435 \u0431\u0443\u0434\u0435\u0442 \u0440\u0430\u0431\u043E\u0442\u0430\u0442\u044C."] }), _jsxs(AlertActions, { children: [_jsx(Button, { plain: true, disabled: removingId !== null, onClick: () => setConfirmingRemove(null), children: "\u041E\u0442\u043C\u0435\u043D\u0430" }), _jsx(Button, { color: "red", disabled: confirmingRemove === null || removingId !== null, onClick: () => {
                                    if (confirmingRemove)
                                        void onRemove(confirmingRemove);
                                }, children: "\u0423\u0434\u0430\u043B\u0438\u0442\u044C" })] })] }), _jsxs(Alert, { open: alertError !== null, onClose: (open) => {
                    if (!open)
                        setAlertError(null);
                }, children: [_jsx(AlertTitle, { children: alertError?.title }), _jsx(AlertDescription, { children: alertError?.message }), _jsx(AlertActions, { children: _jsx(Button, { onClick: () => setAlertError(null), children: "\u041F\u043E\u043D\u044F\u0442\u043D\u043E" }) })] })] }));
};
const AccountNameSection = (props) => {
    const { data } = props;
    const personalHub = data.hubs.find((hub) => hub.hub_id === data.personal_hub_id);
    const displayName = data.username.trim() || personalHub?.hub_name || "—";
    const { mutate } = useSWRConfig();
    return (_jsx(InlineNameEditor, { label: "\u0418\u043C\u044F", value: data.username, defaultEditing: !data.username.trim(), displayValue: displayName, className: "col-span-full sm:grid-cols-subgrid!", onSave: async (username) => {
            await apiSetUserName({ username });
            await mutate(urlApiProfile);
        } }));
};
const AccountInfo = (props) => {
    const { data } = props;
    const { requestLogout } = useAccount();
    const { mutate } = useSWRConfig();
    const [confirmingReissueLoginKey, setConfirmingReissueLoginKey] = useState(false);
    const [isReissuingLoginKey, setIsReissuingLoginKey] = useState(false);
    const [reissueError, setReissueError] = useState(null);
    const [showingLoginLinkQr, setShowingLoginLinkQr] = useState(false);
    const qrUrl = buildLinkQrUrl(data.login_link);
    const reissueLoginKey = async () => {
        if (isReissuingLoginKey)
            return;
        setIsReissuingLoginKey(true);
        setReissueError(null);
        try {
            const body = { token: data.login_key };
            await apiReissueLoginKey(body);
            await mutate(urlApiProfile);
            setConfirmingReissueLoginKey(false);
        }
        catch (err) {
            setReissueError(err instanceof Error ? err.message : String(err));
        }
        finally {
            setIsReissuingLoginKey(false);
        }
    };
    return (_jsxs(DescriptionList, { className: "grid-cols-1! gap-x-4 sm:grid-cols-[min(30%,--spacing(64))_minmax(0,1fr)_max-content]!", children: [_jsx(DescriptionTerm, { className: "col-span-full sm:col-span-1 sm:col-start-1", children: "\u0418\u043C\u044F" }), _jsx(DescriptionDetails, { className: accountDetailsClassName, children: _jsx(AccountNameSection, { data: data }) }), _jsx(DescriptionTerm, { className: "col-span-full sm:col-span-1 sm:col-start-1", children: "\u0412\u0445\u043E\u0434 \u043F\u043E email" }), _jsx(DescriptionDetails, { className: accountDetailsClassName, children: _jsx(EmailSection, {}) }), _jsx(DescriptionTerm, { className: "col-span-full sm:col-span-1 sm:col-start-1", children: "\u0412\u0445\u043E\u0434 \u043F\u043E passkey" }), _jsx(DescriptionDetails, { className: accountDetailsClassName, children: _jsx(PasskeySection, {}) }), _jsx(DescriptionTerm, { className: "col-span-full sm:col-span-1 sm:col-start-1", children: "\u0412\u0445\u043E\u0434 \u043F\u043E \u0441\u0441\u044B\u043B\u043A\u0435" }), _jsx(DescriptionDetails, { className: accountDetailsClassName, children: _jsxs(Field, { className: "col-span-full grid min-w-0 grid-cols-subgrid", children: [_jsx(Label, { className: "col-span-full col-start-1 sm:col-span-1 sm:col-start-1", children: "\u0421\u0435\u043A\u0440\u0435\u0442\u043D\u0430\u044F \u0441\u0441\u044B\u043B\u043A\u0430" }), _jsx(Description, { className: "col-span-full col-start-1 mb-[18px] sm:col-span-1 sm:col-start-1", children: "\u041E\u0442\u043F\u0440\u0430\u0432\u044C\u0442\u0435 \u043D\u0430 \u0434\u0440\u0443\u0433\u0438\u0435 \u0443\u0441\u0442\u0440\u043E\u0439\u0441\u0442\u0432\u0430 \u0434\u043B\u044F \u0432\u0445\u043E\u0434\u0430 \u0431\u0435\u0437 \u043F\u0430\u0440\u043E\u043B\u044F" }), _jsxs("div", { className: "col-span-full grid min-w-0 grid-cols-subgrid items-center", children: [_jsx(CopyableValue, { value: data.login_link, inputAriaLabel: "\u0421\u0435\u043A\u0440\u0435\u0442\u043D\u0430\u044F \u0441\u0441\u044B\u043B\u043A\u0430", className: "min-w-0", unbounded: true }), _jsx(Button, { plain: true, className: accountActionClassName, disabled: isReissuingLoginKey, onClick: () => setConfirmingReissueLoginKey(true), children: isReissuingLoginKey ? "Перевыпускаем…" : "Перевыпустить" })] }), _jsx("div", { className: "col-span-full col-start-1 mt-2 sm:col-span-1 sm:col-start-1", children: _jsxs(Text, { children: ["\u0414\u043E\u0431\u0430\u0432\u043B\u0435\u043D\u0430: ", formatTimestamp(data.login_key_added_at)] }) }), _jsx("div", { className: "col-span-full mt-[18px]", children: _jsxs(Button, { outline: true, onClick: () => setShowingLoginLinkQr(true), children: [_jsx(QrCodeIcon, {}), "\u041F\u043E\u043A\u0430\u0437\u0430\u0442\u044C QR-\u043A\u043E\u0434"] }) }), _jsxs(Alert, { open: showingLoginLinkQr, onClose: setShowingLoginLinkQr, children: [_jsx(AlertTitle, { children: "QR-\u043A\u043E\u0434 \u0434\u043B\u044F \u0432\u0445\u043E\u0434\u0430" }), _jsx(AlertDescription, { children: "\u041E\u0442\u0441\u043A\u0430\u043D\u0438\u0440\u0443\u0439\u0442\u0435 \u043A\u043E\u0434 \u0434\u043B\u044F \u0432\u0445\u043E\u0434\u0430 \u043D\u0430 \u0434\u0440\u0443\u0433\u043E\u043C \u0443\u0441\u0442\u0440\u043E\u0439\u0441\u0442\u0432\u0435." }), _jsx(AlertBody, { children: _jsx("div", { className: "flex justify-center", children: _jsx("img", { alt: "QR-\u043A\u043E\u0434 \u0434\u043B\u044F \u0432\u0445\u043E\u0434\u0430", className: "object-contain w-full max-w-64", src: qrUrl }) }) }), _jsx(AlertActions, { children: _jsx(Button, { plain: true, onClick: () => setShowingLoginLinkQr(false), children: "\u0417\u0430\u043A\u0440\u044B\u0442\u044C" }) })] }), _jsxs(Alert, { open: confirmingReissueLoginKey, onClose: (open) => {
                                if (isReissuingLoginKey)
                                    return;
                                setConfirmingReissueLoginKey(open);
                            }, children: [_jsx(AlertTitle, { children: "\u041F\u0435\u0440\u0435\u0432\u044B\u043F\u0443\u0441\u0442\u0438\u0442\u044C \u0441\u0435\u043A\u0440\u0435\u0442\u043D\u0443\u044E \u0441\u0441\u044B\u043B\u043A\u0443?" }), _jsx(AlertDescription, { children: "\u0412\u0445\u043E\u0434 \u043F\u043E \u0442\u0435\u043A\u0443\u0449\u0435\u0439 \u0441\u0435\u043A\u0440\u0435\u0442\u043D\u043E\u0439 \u0441\u0441\u044B\u043B\u043A\u0435 \u0431\u043E\u043B\u044C\u0448\u0435 \u043D\u0435 \u0431\u0443\u0434\u0435\u0442 \u0440\u0430\u0431\u043E\u0442\u0430\u0442\u044C." }), _jsxs(AlertActions, { children: [_jsx(Button, { plain: true, disabled: isReissuingLoginKey, onClick: () => setConfirmingReissueLoginKey(false), children: "\u041E\u0442\u043C\u0435\u043D\u0430" }), _jsx(Button, { color: "orange", disabled: isReissuingLoginKey, onClick: () => void reissueLoginKey(), children: isReissuingLoginKey ? "Перевыпускаем…" : "Перевыпустить" })] })] }), _jsxs(Alert, { open: reissueError !== null, onClose: (open) => {
                                if (!open)
                                    setReissueError(null);
                            }, children: [_jsx(AlertTitle, { children: "\u041D\u0435 \u0443\u0434\u0430\u043B\u043E\u0441\u044C \u043F\u0435\u0440\u0435\u0432\u044B\u043F\u0443\u0441\u0442\u0438\u0442\u044C \u0441\u0441\u044B\u043B\u043A\u0443" }), _jsx(AlertDescription, { children: reissueError }), _jsx(AlertActions, { children: _jsx(Button, { onClick: () => setReissueError(null), children: "\u041F\u043E\u043D\u044F\u0442\u043D\u043E" }) })] })] }) }), _jsx(DescriptionTerm, { className: "col-span-full sm:col-span-1 sm:col-start-1", children: "\u0412\u044B\u0445\u043E\u0434 \u0438\u0437 \u0430\u043A\u043A\u0430\u0443\u043D\u0442\u0430" }), _jsx(DescriptionDetails, { className: "col-span-full min-w-0 sm:col-span-2", children: _jsx("div", { children: _jsx(Button, { outline: true, onClick: requestLogout, children: "\u0412\u044B\u0439\u0442\u0438 \u043D\u0430 \u044D\u0442\u043E\u043C \u0443\u0441\u0442\u0440\u043E\u0439\u0441\u0442\u0432\u0435" }) }) })] }));
};
const Account = () => {
    const { data, error, isLoading } = useProfile();
    const [location] = useLocation();
    const hubs = /^\/account\/hubs\/?$/i.test(location);
    return (_jsxs(_Fragment, { children: [_jsx(Heading, { className: "mb-4", children: "\u0410\u043A\u043A\u0430\u0443\u043D\u0442" }), _jsx(PageTabs, { label: "\u0420\u0430\u0437\u0434\u0435\u043B\u044B \u0430\u043A\u043A\u0430\u0443\u043D\u0442\u0430", tabs: [
                    { label: "Настройка аккаунта", href: "/account" },
                    { label: "Управление хабами", href: "/account/hubs" },
                ], activeHref: hubs ? "/account/hubs" : "/account" }), _jsxs("div", { className: "mt-8 sm:mt-10", children: [isLoading && _jsx(LoadingBadge, {}), error && _jsx(LoadingErrorBadge, {}), data &&
                        (hubs ? (_jsx(AccountHubs, { hubs: data.hubs })) : (_jsx(AccountInfo, { data: data })))] })] }));
};
export default Account;
